<template>
  <div class="orders-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h3 class="page-title mb-1">Мои ордера</h3>
        <div class="page-subtitle">
          История ваших торговых заявок. Кликните по карточке для деталей.
        </div>
      </div>

      <div class="d-flex">
        <b-button variant="outline-light" class="btn-round mr-2" @click="loadOrders">
          ↻ Обновить
        </b-button>
        <b-button variant="primary" class="cta-btn" @click="$router.push('/buy/BUY')">
          + Создать ордер
        </b-button>
      </div>
    </div>

    <!-- Filter chips: преcет «Все», активные, завершённые, неуспешные -->
    <div class="filter-row">
      <button
        v-for="opt in filterOptions"
        :key="opt.key"
        type="button"
        class="filter-chip"
        :class="{ 'filter-chip-active': activeFilter === opt.key }"
        @click="activeFilter = opt.key"
      >{{ opt.label }} <span v-if="counts[opt.key] != null" class="chip-cnt">{{ counts[opt.key] }}</span></button>
    </div>

    <!-- Orders grid -->
    <div v-if="filteredOrders.length" class="orders-list">
      <div
        v-for="order in filteredOrders"
        :key="order.orderId"
        class="ord-card"
        @click="$router.push('/order/' + order.orderId + '/wait')"
      >
        <div class="ord-head">
          <div class="ord-head-left">
            <span class="ord-id">#{{ order.orderId }}</span>
            <span class="ord-type" :class="'ord-type-' + order.exType.toLowerCase()">{{ order.exType }}</span>
            <span class="ord-status" :class="statusCss(effective(order))">{{ statusLabel(effective(order)) }}</span>
          </div>
          <div class="ord-time">{{ formatRelative(order.lockedAt || order.createdAt) }}</div>
        </div>

        <div class="ord-flow">
          <div class="ord-side">
            <div class="ord-side-lbl">Списано</div>
            <div class="ord-side-val">{{ formatAmount(order.sellAmount, order.sellAsset) }}</div>
          </div>
          <div class="ord-arrow">→</div>
          <div class="ord-side ord-side-buy">
            <div class="ord-side-lbl">К получению</div>
            <div class="ord-side-val">{{ formatAmount(order.buyAmount, order.buyAsset) }}<span v-if="order.buyNetwork" class="ord-net">{{ order.buyNetwork }}</span></div>
          </div>
        </div>

        <div class="ord-meta">
          <span class="ord-rate">Курс {{ formatRate(order.lockedPrice) }} USD за 1 {{ cryptoSide(order) }}</span>
          <span class="ord-meta-sep">·</span>
          <span class="ord-fee">Комиссия {{ formatAmount(order.companyFeeUsd, 'USD') }}</span>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading" class="empty">
      <div class="empty-icon">📋</div>
      <div class="empty-title">
        {{ activeFilter === 'ALL' ? 'У вас пока нет ордеров' : 'По выбранному фильтру ничего нет' }}
      </div>
      <div class="empty-subtitle">
        {{ activeFilter === 'ALL' ? 'Нажмите «Создать ордер», чтобы оформить первую заявку.' : 'Переключите фильтр чтобы увидеть остальные заявки.' }}
      </div>
      <b-button v-if="activeFilter === 'ALL'" variant="primary" class="cta-btn mt-3" @click="$router.push('/buy/BUY')">
        + Создать ордер
      </b-button>
    </div>

    <div v-else class="loader">
      <b-spinner small class="mr-2"></b-spinner>
      Загрузка…
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'

// Активные статусы — ордер ещё «живой», требует или ждёт действия.
// Используется и для дефолтного preset'а, и для счётчиков.
const ACTIVE_STATUSES = ['PENDING', 'AWAITING_RECONFIRM', 'EXECUTING']
const COMPLETED_STATUSES = ['COMPLETED']
const FAILED_STATUSES = ['EXPIRED', 'CANCELLED', 'REJECTED']

export default {
  name: 'MyOrders',
  data() {
    return {
      orders: [],
      loading: false,
      activeFilter: 'ALL',
      filterOptions: [
        { key: 'ALL',       label: 'Все' },
        { key: 'ACTIVE',    label: 'Активные' },
        { key: 'COMPLETED', label: 'Исполненные' },
        { key: 'FAILED',    label: 'Отменённые' },
      ],
    }
  },
  computed: {
    counts() {
      return {
        ALL:       this.orders.length,
        ACTIVE:    this.orders.filter((o) => ACTIVE_STATUSES.includes(this.effective(o))).length,
        COMPLETED: this.orders.filter((o) => COMPLETED_STATUSES.includes(this.effective(o))).length,
        FAILED:    this.orders.filter((o) => FAILED_STATUSES.includes(this.effective(o))).length,
      }
    },
    filteredOrders() {
      if (this.activeFilter === 'ALL') return this.orders
      const sets = {
        ACTIVE:    ACTIVE_STATUSES,
        COMPLETED: COMPLETED_STATUSES,
        FAILED:    FAILED_STATUSES,
      }
      const allow = sets[this.activeFilter] || []
      return this.orders.filter((o) => allow.includes(this.effective(o)))
    },
  },
  methods: {
    loadOrders() {
      this.loading = true
      axios.get(`${apiDomain}/exchange/orders`, { headers: getHeader() })
        .then((res) => { this.orders = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.orders = [] })
        .finally(() => { this.loading = false })
    },
    effective(order) {
      // Бэкенд проецирует EXPIRED через effectiveStatus — используем
      // его если есть, иначе сырой status.
      return (order.effectiveStatus || order.status || '').toUpperCase()
    },
    cryptoSide(order) {
      return order.exType === 'BUY' ? order.buyAsset : order.sellAsset
    },
    statusLabel(status) {
      return ({
        PENDING:            'В очереди',
        AWAITING_RECONFIRM: 'Жд. подтв.',
        EXECUTING:          'Исполняется',
        COMPLETED:          'Исполнен',
        EXPIRED:            'Истёк',
        CANCELLED:          'Вы отменили',
        REJECTED:           'Отклонён',
      })[status] || status
    },
    statusCss(status) {
      return ({
        PENDING:            'st-pending',
        AWAITING_RECONFIRM: 'st-warn',
        EXECUTING:          'st-info',
        COMPLETED:          'st-ok',
        EXPIRED:            'st-muted',
        CANCELLED:          'st-muted',
        REJECTED:           'st-no',
      })[status] || ''
    },
    formatAmount(value, asset) {
      const fractionDigits = asset === 'USD' ? 2 : 8
      const n = Number(value || 0)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: asset === 'USD' ? 2 : 0,
        maximumFractionDigits: fractionDigits,
      }).format(n) + ' ' + (asset || '')
    },
    formatRate(value) {
      const n = Number(value || 0)
      const digits = n >= 100 ? 2 : n >= 1 ? 4 : 6
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(n)
    },
    formatRelative(iso) {
      if (!iso) return '—'
      const diff = Date.now() - Date.parse(iso)
      if (!Number.isFinite(diff)) return '—'
      const minutes = Math.round(diff / 60000)
      if (minutes < 1)  return 'только что'
      if (minutes < 60) return minutes + ' мин назад'
      const hours = Math.round(minutes / 60)
      if (hours < 24)   return hours + ' ч назад'
      const days = Math.round(hours / 24)
      if (days < 7)     return days + ' дн назад'
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
  },
  created() {
    this.loadOrders()
  },
}
</script>

<style scoped>
.orders-page { margin-top: 18px; padding-bottom: 60px; }
.page-title { color: #FCFCFD; font-weight: 800; letter-spacing: -0.3px; margin-top: 6px; }
.page-subtitle { color: #B1B5C3; font-size: 13px; max-width: 540px; }
.btn-round { border-radius: 12px; padding: 8px 14px; }
.cta-btn {
  border-radius: 12px; padding: 8px 16px;
  background: #efb914; border-color: #efb914;
  color: #1a1d22; font-weight: 700;
}
.cta-btn:hover { filter: brightness(1.05); }

.filter-row {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-bottom: 16px;
}
.filter-chip {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  color: #B1B5C3;
  border-radius: 999px;
  padding: 7px 14px;
  font: 700 12px/1 Inter;
  cursor: pointer;
  transition: all .12s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.filter-chip:hover { color: #FCFCFD; border-color: rgba(119,126,144,.4); }
.filter-chip-active {
  background: #efb914; border-color: #efb914; color: #1a1d22;
}
.filter-chip-active:hover { color: #1a1d22; }
.chip-cnt {
  background: rgba(0,0,0,0.18); border-radius: 8px;
  padding: 1px 6px; font-size: 10.5px;
}
.filter-chip-active .chip-cnt { background: rgba(0,0,0,0.18); color: #1a1d22; }

.orders-list { display: flex; flex-direction: column; gap: 10px; }

.ord-card {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all .15s;
}
.ord-card:hover {
  background: rgba(35,38,47,.75);
  border-color: rgba(239,185,20,.3);
  transform: translateY(-1px);
}

.ord-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.ord-head-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ord-id { color: #777e90; font: 700 12px/1 Inter; }
.ord-type {
  font: 700 11px/1 Inter;
  padding: 3px 9px;
  border-radius: 5px;
  letter-spacing: .02em;
}
.ord-type-buy  { background: rgba(16,185,129,0.15); color: #34D399; }
.ord-type-sell { background: rgba(96,165,250,0.15); color: #93C5FD; }

.ord-status {
  font: 700 10.5px/1 Inter;
  padding: 3px 9px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.st-pending { background: rgba(239,185,20,0.15); color: #FBBF24; }
.st-warn    { background: rgba(239,68,68,0.15); color: #F87171; }
.st-info    { background: rgba(96,165,250,0.15); color: #93C5FD; }
.st-ok      { background: rgba(16,185,129,0.15); color: #34D399; }
.st-muted   { background: rgba(156,163,175,0.15); color: #9CA3AF; }
.st-no      { background: rgba(239,68,68,0.15); color: #F87171; }

.ord-time { color: #777e90; font-size: 11.5px; flex-shrink: 0; }

.ord-flow {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid rgba(119,126,144,.12);
  border-bottom: 1px solid rgba(119,126,144,.12);
  margin-bottom: 10px;
}
.ord-side { flex: 1; min-width: 0; }
.ord-side-lbl {
  color: #777e90;
  font: 700 9.5px/1 Inter;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 4px;
}
.ord-side-val { color: #FCFCFD; font: 700 14px/1.2 Inter; }
.ord-side-buy .ord-side-val { color: #efb914; }
.ord-net {
  font-size: 9.5px;
  background: rgba(239,185,20,.18);
  color: #efb914;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  font-weight: 700;
}
.ord-arrow { color: #777e90; font-size: 18px; }

.ord-meta {
  font-size: 11.5px;
  color: #777e90;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ord-meta-sep { color: #4b4d56; }

.empty { text-align: center; padding: 60px 20px; color: #B1B5C3; }
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-title { font: 700 18px/1 Inter; color: #FCFCFD; margin-bottom: 6px; }
.empty-subtitle { font-size: 13px; color: #777e90; max-width: 420px; margin: 0 auto; }

.loader { text-align: center; padding: 30px 20px; color: #B1B5C3; font-size: 13px; }
</style>
